import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { ExpertServiceCard } from './expert-service-card';
import { SupplierTable } from './supplier-table';
import { Button, Checkbox, Label } from '@leadsnavi/ui-kit';
import { useSupplierListModel } from './use-supplier-model';
import { SupplierListDetail } from './supplier-list-page';
import { CompBlockProps } from '@/features/chat-messages/types/comp-block';
import { PageDialogWrapper } from '../base/pageDialogWrapper';
import { IApiSubMessageDto } from '@/services/swagger/easourcing/models';
import { useMount } from 'ahooks';

import { SearchCardSubMsg } from '../../types';
import { SupplierDetailDrawer } from './supplier-detail/supplier-detail';
import CountrySelect from '@/common/base/country-select';
import { SupplierCardContext } from '../../store/supplier-card-context';
import { isString } from 'lodash-es';
import { createSupplierListStore, SupplierListStoreContext, useChatPageStore, useSupplierListStore } from '../../store';
import mixpanel from 'mixpanel-browser';
import { ResultSummary } from './components/result-summary';
import { BaseTooltip } from '@/common/base/tooltip';

/**
 * 在回话消息里展示的搜索任务列表卡片
 * @param props
 * @returns
 */
export const BaseSupplierChatListCard = () => {
  const isTopFviewOtherDataLoading = useSupplierListStore(state => state.isTopFviewOtherDataLoading);
  const isTopFviewSummaryLoading = useSupplierListStore(state => state.isTopFviewSummaryLoading);
  const supplierTotal = useSupplierListStore(state => state.supplierListData?.total || 0);
  const topFiveSuppliers = useSupplierListStore(state => state.topFiveSuppliers);
  const setStoreState = useSupplierListStore(state => state.setStoreState);
  const {
    subMsgData,
    selectedCount,
    selectFilter,
    onSelectChange,
    onVisibleDetailChange,
    onSearchChange,
    searchFilter,
    showSkeleton,
    onSelectAllClick,
  } = useContext(SupplierCardContext);
  const queryProduct = subMsgData?.contentValue.queryProduct || '';

  return (
    <div className=" mt-3   bg-[#F5F5F5] p-5 rounded-xl">
      <div className="mb-3">
        <h2 className="text-xl font-semibold  mb-2 self-stretch text-[color:var(--800-fill-,#262626)] [font-family:Geist]  ">
          {queryProduct} Recommended Suppliers
        </h2>
        <p className=" text-[color:var(--800-fill-,#262626)] text-sm [font-family:Geist]  ">
          Our AI has found the following quality suppliers for you, while our expert team will manually screen more
          options
        </p>
      </div>

      <ExpertServiceCard />

      <div className="flex justify-between items-center mb-4 mt-6">
        <p className="text-sm text-[#262626]">
          Meanwhile, by analyzing the trade database, we have compiled the following companies.
        </p>
      </div>
      <div className=" flex items-center gap-3 mb-3 justify-between">
        <div className="w-1/2  flex gap-1 items-center">
          <CountrySelect
            className="w-1/2"
            size="smallMedium"
            mode="multiple"
            placeholder="Select supplier country"
            value={searchFilter.country}
            onChange={value => {
              onSearchChange({
                country: isString(value) ? [value] : value,
              });
            }}
          />
        </div>
        <div className="inline-flex items-center gap-2">
          <Checkbox
            checked={searchFilter.filterEdm}
            id="no-outreach"
            onCheckedChange={value => {
              onSearchChange({
                filterEdm: value as boolean,
              });
            }}
          />
          <BaseTooltip content={'Uncontacted suppliers'}>
            <span>
              <Label
                className=" cursor-pointer text-[color:var(--600-fill-,#525252)] [font-family:Geist] text-sm font-normal leading-[150%] tracking-[0.07px]"
                htmlFor="no-outreach"
              >
                Uncontacted
              </Label>
            </span>
          </BaseTooltip>
        </div>
        {/* <div className="flex items-center gap-1">
          <MultipleSelect
            options={languageOptions || []}
            placeholder="language"
            emptyMessage="No options"
            className=" min-w-40 h-9 bg-white"
            selectionMode="multiple"
            ellipsis={true}
            value={searchSelectedLanguages}
            onChange={values => {
              onSearchChange({
                otherLanguages: values.map(splitLanguage),
              });
            }}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Query1Icon className="" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-[315px]">
              <p>
                The system will search using the English version of your keywords, as well as any languages you
                explicitly request. You can also choose to translate them into additional languages to expand your
                search.
              </p>
            </TooltipContent>
          </Tooltip>
        </div> */}
      </div>
      <div className="bg-white rounded-[6px] overflow-hidden">
        <SupplierTable
          isKeywordLoading={isTopFviewOtherDataLoading}
          isAiSummaryLoding={isTopFviewSummaryLoading}
          suppliers={topFiveSuppliers}
          showSkeleton={showSkeleton}
          selectFilter={selectFilter}
          onSelectChange={onSelectChange}
          onDetailClick={onVisibleDetailChange}
          onSelectAllClick={onSelectAllClick}
        />
      </div>

      <div className="flex  justify-between items-center mt-3">
        <div>
          <ResultSummary total={supplierTotal} selectedCount={selectedCount} />
        </div>
        {supplierTotal > 5 ? (
          <Button
            // className="flex items-center gap-1 text-sm text-gray-900 hover:text-black transition-colors"
            variant={'outline'}
            className="h-8 gap-[6px] text-[#262626]"
            id="search_seemore_click"
            onClick={() => {
              mixpanel.track('search_seemore_click');
              setStoreState({ showListDetailSkeleton: true, showListDetailModal: true });
            }}
          >
            See More
            <ChevronDown className=" -rotate-90" size={16} />
          </Button>
        ) : null}
      </div>
    </div>
  );
};
/**
 * 供应商列表卡片组件
 * @param props
 * @returns
 */
export const PureSupplierChatListCard: React.FC<
  CompBlockProps & {
    subMsgData: IApiSubMessageDto;
    messageId: string;
  }
> = props => {
  const { dialogWrpperRef, subMsgData: _subMsgData, sessionId, messageId } = props;
  const subMsgData = _subMsgData as unknown as SearchCardSubMsg;
  const showListDetailModal = useSupplierListStore(state => state.showListDetailModal);
  const aggregatedSuppliers = useSupplierListStore(state => state.aggregatedSuppliers);
  const isSummaryLoading = useSupplierListStore(state => state.isSummaryLoading);
  const isOtherDataLoading = useSupplierListStore(state => state.isOtherDataLoading);
  const getStoreState = useChatPageStore(state => state.getStoreState);

  const supplierListModel = useSupplierListModel({
    sessionId,
    messageId: messageId,
    subMessageId: subMsgData.subMessageId,
    subMsgData,
  });
  const { isAiSummaryLoding, visibleDetail, onVisibleDetailChange, selectFilter, getSupplierListByInit } =
    supplierListModel;

  //
  // 6. 如果是 sse 消息（ai 回答的消息），则需要默认全选
  const isSseMsg = getStoreState().sseMessageIds.has(messageId);
  const hasTrackedSearchListDone = useRef(false);
  useEffect(() => {
    if (isSseMsg && aggregatedSuppliers && aggregatedSuppliers.length && !isSummaryLoading && !isOtherDataLoading) {
      if (hasTrackedSearchListDone.current) return;
      hasTrackedSearchListDone.current = true;
      console.log(1111, 'search_list_done');
      mixpanel.track('search_list_done');
    }
  }, [aggregatedSuppliers, isSummaryLoading, isOtherDataLoading, isSseMsg]);

  useMount(() => {
    getSupplierListByInit();
  });
  const selectedCount = useMemo(() => {
    return selectFilter?.includeIds?.length ?? 0;
  }, [selectFilter]);
  return (
    <SupplierCardContext.Provider
      value={{
        ...supplierListModel,
        subMsgData,
        selectedCount,
      }}
    >
      <BaseSupplierChatListCard />
      {/* 展示供应商详情弹窗 */}
      {showListDetailModal && dialogWrpperRef?.current
        ? ReactDOM.createPortal(
            <PageDialogWrapper>
              <div className="pt-12 h-full">
                <SupplierListDetail />
              </div>
            </PageDialogWrapper>,
            dialogWrpperRef.current
          )
        : null}
      <SupplierDetailDrawer
        traceId="search_company_detail_view"
        isAiSummaryLoding={isAiSummaryLoding}
        visible={!!visibleDetail}
        companyId={visibleDetail?.companyId}
        supplier={visibleDetail}
        onClose={() => {
          onVisibleDetailChange(null);
        }}
      />
    </SupplierCardContext.Provider>
  );
};

export const SupplierChatListCard: React.FC<React.ComponentProps<typeof PureSupplierChatListCard>> = props => {
  const [supplierListStore] = useState(() => {
    return createSupplierListStore({
      messageId: props.messageId,
      sessionId: props.sessionId,
      subMessageId: props.subMsgData.subMessageId,
      subMsgData: props.subMsgData,
    });
  });
  return (
    <SupplierListStoreContext.Provider value={supplierListStore}>
      <PureSupplierChatListCard {...props} />
    </SupplierListStoreContext.Provider>
  );
};
